import { SectionData, TeaserData } from "@app/types/ServerCustomContext";
import AnchorCarousel from "@components/Modules/AnchorCarousel/AnchorCarousel";
import AppExploreMore from "@components/Modules/AppExploreMore/AppExploreMore";
import { configCategory, configExploreMore, configTeaser } from "@pub/config";
import placeholders from "@pub/placeholders";
import {
  Ads,
  AnchorBlock,
  Card,
  CustomClassContext,
  Divider,
  ExploreMore,
  ListGroup,
  SectionContext,
  Tab,
  Typography,
  UserSubmissionBox,
} from "@sphtech/dmg-design-system";
import { NoSSR } from "@sphtech/web2-core/components";
import cx from "classnames";
import { Fragment, ReactNode, useContext } from "react";

import styles from "./category.module.css";
import CategoryStoryListing from "./CategoryStoryListing";
import CategoryStoryListingLayout from "./CategoryStoryListingLayout";
import SubAnchor from "./SubAnchor";
import SubAnchorVideo from "./SubAnchorVideo";

const { Heading, Label } = Typography;

type AnchorBlockProps = React.ComponentProps<typeof AnchorBlock> & {
  id?: string;
};

type videoGalleryLandscapeProps = {
  title?: string;
  teasers: TeaserData[];
};

type Props = {
  data: {
    uniqSectionName?: string;
    tagSlug?: string;
    name: string;
    nameZH?: string;
    subHeading?: string;
    listGroup?: React.ComponentProps<typeof ListGroup>;
    anchorCarousel?: TeaserData[];
    anchorBlock?: AnchorBlockProps;
    latestArticle?: TeaserData[];
    exploreMore?: React.ComponentProps<typeof ExploreMore>;
    videoGallery?: videoGalleryLandscapeProps;
    mainContentBlock?: TeaserData[];
    lb1?: ReactNode;
    imu1?: ReactNode;
    imu2?: ReactNode;
    userSubmissionBox?: UserSubmissionBox;
    showVerticalLine?: boolean;
    queryLegacyTagData?: boolean;
    storyType?: SectionData["storyType"];
    path?: SectionData["path"];
  };
  variant?: "default" | "carousel";
};

function CategoryLayout({ data, variant = "default" }: Props): ReactNode {
  const uniqueSectionNameOrTag = data.uniqSectionName ?? data.tagSlug;
  const ignoreIds = [] as string[];
  const isSectionPage = !!data.uniqSectionName;
  const mobileAdBeforeVideoGallery =
    isSectionPage && configCategory.videoGalleryLandscape?.display;

  if (data.anchorBlock?.id) {
    ignoreIds.push(data.anchorBlock.id);
  }

  if (data.latestArticle) {
    data.latestArticle.forEach((item) => {
      if (item.id) {
        ignoreIds.push(item.id);
      }
    });
  }

  if (data.mainContentBlock) {
    data.mainContentBlock.forEach((item) => {
      if (item.id) {
        ignoreIds.push(item.id);
      }
    });
  }

  const customClass = useContext(CustomClassContext);
  const sectionContainerClass = customClass?.categoryLayout?.sectionContainer;
  const categoryLayout = configCategory.card;
  const tabPath =
    data.path || (data.tagSlug && `/tag/${data.tagSlug}`) || "/latest-stories";

  return (
    <Fragment>
      {/* {Category Info} */}
      <section
        className={cx(
          styles.bodyContainer,
          styles.categoryInfoContainer,
          sectionContainerClass,
        )}
      >
        {/* Display Category Name */}
        {data.name && (
          <div
            className={cx(
              styles.headingContainer,
              styles[data.listGroup ? "headingWithTab" : "headingStandalone"],
              data.showVerticalLine && styles.verticalLine,
              customClass?.categoryLayout?.categoryHeading,
              data.tagSlug && customClass?.tagLayout?.headingContainer,
            )}
          >
            {data.subHeading && <Label Element="span">{data.subHeading}</Label>}

            <Heading.SectionTitle nameZH={data.nameZH}>
              {data.name}
            </Heading.SectionTitle>
          </div>
        )}

        {/* Display Submenu */}
        {data.listGroup && (
          <div
            className={cx(
              styles.tabContainer,
              data.showVerticalLine && styles.verticalLine,
              customClass?.categoryLayout?.tabContainer,
            )}
          >
            <ListGroup
              variant={configCategory.listGroupVariant}
              {...data.listGroup}
            />

            {!configCategory.hideSubmenuDivider && <Divider />}
          </div>
        )}

        {!!configCategory.filterBy?.options?.length && (
          <Tab
            label={configCategory.filterBy.label}
            data={configCategory.filterBy.options.map((option) => {
              const isSelected = data.storyType === option.storyType;
              return {
                text: option.text,
                url: isSelected
                  ? tabPath // unselect if selected
                  : `${tabPath}/${option.pathSuffix}`,
                isSelected,
              };
            })}
          />
        )}
      </section>

      {/* AnchorBlock */}
      {variant === "default" && data.anchorBlock && (
        <section
          className={cx(
            styles.bodyContainer,
            styles.gutterBottomOnly,
            styles.categoryAnchorBlockContainer,
            sectionContainerClass,
            customClass?.categoryLayout?.anchorBlock,
          )}
        >
          {categoryLayout ? (
            <Card
              {...data.anchorBlock}
              variant={categoryLayout.anchorblock.defaultVariant}
              mobileUpVariant={
                categoryLayout.anchorblock.mobileUp.defaultVariant
              }
              placeholders={placeholders}
              displayPublishDate={configTeaser.anchorBlock?.publishDate}
            />
          ) : (
            <AnchorBlock
              {...data.anchorBlock}
              placeholder={placeholders.landscape}
              displayPublishDate={configTeaser.anchorBlock?.publishDate}
              mobileCrop={configTeaser.anchorBlock?.mobileCrop}
            />
          )}
        </section>
      )}

      {/* Just below AnchorBlock */}
      {variant === "default" &&
        data.latestArticle &&
        data.latestArticle.length > 0 && (
          <section
            className={cx(
              styles.bodyContainer,
              styles.gutterBottom,
              sectionContainerClass,
            )}
          >
            <div className={styles.latestTeaserContainer}>
              <div className={styles.latestTeaserLayout}>
                <SubAnchor articleList={data.latestArticle} />
                {mobileAdBeforeVideoGallery && (
                  <div className={styles.mobileAdvertisement}>
                    <Ads.AdsSideBarMobile />
                  </div>
                )}
                <SubAnchorVideo videoGallery={data.videoGallery} />
              </div>

              <div className={styles.sidebar}>
                {mobileAdBeforeVideoGallery ? (
                  <Ads.AdsSideBarDesktop />
                ) : (
                  <Ads.AdsSideBar />
                )}

                {configCategory.exploreMoreInPinned &&
                  uniqueSectionNameOrTag && (
                    <div className={styles.pinnedExploreMore}>
                      <NoSSR>
                        <SectionContext.Provider value={{}}>
                          <AppExploreMore
                            uniqSectionName={data.uniqSectionName}
                            tagName={data.tagSlug}
                            headingText={configExploreMore.heading}
                            headingTextZH={configExploreMore.headingZH}
                            render={(children) => (
                              <div
                                className={cx(
                                  styles.exploreMoreContainer,
                                  customClass?.categoryLayout
                                    ?.exploreMoreContainer,
                                )}
                              >
                                {!configExploreMore.hideDivider && <Divider />}
                                {children}
                                {!configExploreMore.hideDivider && <Divider />}
                              </div>
                            )}
                          />
                        </SectionContext.Provider>
                      </NoSSR>
                    </div>
                  )}
              </div>
            </div>
          </section>
        )}

      {variant === "carousel" && data.anchorCarousel && (
        <div className={styles.categoryCarouselContainer}>
          <AnchorCarousel data={data.anchorCarousel} />

          <div className={styles.mobileAdvertisement}>
            <Ads.AdsSideBarMobile />
          </div>
        </div>
      )}

      {/* Main Content page 1 */}
      {data.mainContentBlock && data.mainContentBlock.length > 0 && (
        <CategoryStoryListingLayout
          data={{
            mainContentBlock: data.mainContentBlock,
            imu: data.imu1,
          }}
        />
      )}

      {
        <NoSSR>
          <CategoryStoryListing
            data={{
              uniqSectionName: data.uniqSectionName,
              tagSlug: data.tagSlug,
              limit: 8,
              ignoreIds: ignoreIds,
              lb1: data.lb1,
              imu: data.imu2,
              userSubmissionBox: data.userSubmissionBox,
              queryLegacyTagData: data.queryLegacyTagData,
              storyType: data.storyType,
            }}
          />
        </NoSSR>
      }
    </Fragment>
  );
}

export default CategoryLayout;
